import { useCallback, useEffect, useRef, useState } from 'react';
import { type Provenance, type ThumbnailSize, fileCategory, isPreviewable } from '@xfile/shared';
import { fill, formatCount, labelFor, useI18n } from '../i18n';
import { useOverlayDismiss } from '../components/dismiss-gesture';
import { Portal } from '../components/Portal';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  XMarkIcon,
} from './icons';
import { FileTypeIcon } from './file-type-icons';
import { provenanceLabelKey } from './labels';
import { formatBytes, formatDateTime } from './format';
import { fileContentUrl, filePreviewUrl, fileThumbnailUrl } from './upload-api';

/**
 * VISIONNEUSE DE DOSSIER (XFILE-20) — aperçu MODAL naviguant dans TOUS les fichiers du
 * dossier courant. Reçoit la liste complète (`files`) + l'index de départ (`startIndex`) et
 * gère en interne la navigation PRÉCÉDENT/SUIVANT, SANS boucle (boutons inactifs aux
 * extrémités) — la navigation cycle sur tous les fichiers, y compris les non prévisualisables.
 *
 * RÉUTILISABLE (XFILE-30) hors de l'explorateur authentifié (ex. future page PUBLIQUE de
 * dossier partagé, visiteur ANONYME) grâce à trois props OPTIONNELLES dont les défauts
 * reproduisent EXACTEMENT le comportement authentifié : `urls` (constructeurs d'URL du média,
 * défaut = `./upload-api`), `showSupervision` (défaut `true` ; à `false` le pied « propriétés »
 * masque provenance/consultations/dernière consultation mais garde TOUJOURS taille + type MIME)
 * et `onCopyLink` (fourni ⇒ bouton « copier le lien » dans l'en-tête).
 *
 * Rendu selon `fileCategory` / `isPreviewable` du fichier COURANT :
 *  - image (GIF/animées incluses) → `<img>` sur la GRANDE vignette `lg` (WebP web-optimisée 1600 px,
 *    XFILE-26 — rapide, et seule à rendre un HEIC dans un navigateur ; repli gracieux `onError`
 *    DIRECTEMENT sur l'icône de type — jamais l'original, XFILE-35) ; vidéo → `<video controls>`
 *    (contenu original authentifié,
 *    `/api/files/:id/content?slot=N`) — média REMONTÉ (`key={file.id}`) pour un changement
 *    INSTANTANÉ (l'ancien média disparaît aussitôt, opacité pendant le chargement du nouveau) ;
 *  - autre type présent (PDF inclus depuis T13) → panneau « non prévisualisable » + icône de
 *    type + « Ouvrir dans un nouvel onglet » (Chrome affiche le PDF) + Télécharger ;
 *  - contenu absent (`hasContent === false`) → panneau « contenu indisponible » (ni aperçu ni
 *    téléchargement).
 *
 * Un PIED de panneau affiche les PROPRIÉTÉS du fichier courant (provenance, taille, nombre de
 * consultations, dernière consultation, type MIME).
 *
 * Accessible : `role="dialog"` + `aria-modal`, fermeture par Échap ET clic sur le fond, PIÈGE
 * DE FOCUS (Tab boucle), flèches ←/→ pour naviguer, indicateur de position vocalisé, focus
 * initial sur « Fermer » et restauration au déclencheur à la fermeture. Rendu via `Portal`.
 */

export interface PreviewFile {
  id: string;
  name: string;
  mimeType: string;
  /** Le contenu est présent sur disque (sinon : panneau « indisponible », pas de téléchargement). */
  hasContent: boolean;
  sizeBytes: number;
  /**
   * PROVENANCE du dépôt (`web` / `pnyx`) — SUPERVISION, affichée dans le pied « propriétés »
   * (T13). OPTIONNELLE : fournie par l'appelant AUTHENTIFIÉ, absente pour un visiteur public.
   */
  provenance?: Provenance;
  /** Nombre de CONSULTATIONS (services complets des octets) — SUPERVISION, T13. Optionnel (public). */
  consultationCount?: number;
  /** Date ISO de dernière consultation, ou `null` si jamais consulté — SUPERVISION, T13. Optionnel (public). */
  lastConsultedAt?: string | null;
}

/**
 * Constructeurs d'URL du média d'un fichier — INJECTABLES pour réutiliser la visionneuse hors
 * de l'explorateur authentifié (ex. future page PUBLIQUE de dossier partagé). Les défauts
 * ({@link DEFAULT_URLS}) reproduisent le comportement authentifié actuel.
 */
export interface PreviewUrls {
  /** Contenu ORIGINAL brut (téléchargement + repli d'image + source vidéo). */
  content(file: PreviewFile): string;
  /** Vignette d'une TAILLE donnée (`lg` pour l'affichage de l'image en visionneuse). */
  thumbnail(file: PreviewFile, size: ThumbnailSize): string;
  /** URL d'OUVERTURE dans un nouvel onglet (inline durci) pour les types non prévisualisables. */
  open(file: PreviewFile): string;
}

/** URLs par défaut : celles de l'explorateur AUTHENTIFIÉ (`./upload-api`), comportement inchangé. */
const DEFAULT_URLS: PreviewUrls = {
  content: (file) => fileContentUrl(file.id),
  thumbnail: (file, size) => fileThumbnailUrl(file.id, size),
  open: (file) => filePreviewUrl(file.id),
};

/** Nombre de grandes vignettes `lg` préchargées dans la direction de navigation courante. */
const PRELOAD_COUNT = 3;

/**
 * Fenêtre de PRÉCHARGEMENT directionnel : dans la `direction` courante, les fichiers IMAGE aux
 * indices `index±1 … index±count` (bornés aux extrémités, JAMAIS l'index courant). On ne
 * précharge que des images (seules rendues via la vignette `lg`) ; un non-image de la fenêtre
 * est simplement ignoré. Helper PUR et déterministe (testable).
 */
export function preloadWindow(
  files: PreviewFile[],
  index: number,
  direction: 'next' | 'prev',
  count: number,
): PreviewFile[] {
  const targets: PreviewFile[] = [];
  for (let step = 1; step <= count; step += 1) {
    const j = direction === 'next' ? index + step : index - step;
    if (j < 0 || j >= files.length) break;
    const candidate = files[j];
    if (candidate && fileCategory(candidate.mimeType, candidate.name) === 'image') {
      targets.push(candidate);
    }
  }
  return targets;
}

export function FilePreview({
  files,
  startIndex,
  onClose,
  urls = DEFAULT_URLS,
  showSupervision = true,
  onCopyLink,
}: {
  files: PreviewFile[];
  startIndex: number;
  onClose: () => void;
  /** Constructeurs d'URL du média (défaut : explorateur authentifié `./upload-api`). */
  urls?: PreviewUrls;
  /**
   * Afficher les infos de SUPERVISION (provenance / consultations / dernière consultation) ?
   * Défaut `true` (explorateur authentifié) ; `false` en usage public — taille + type MIME
   * restent toujours affichés.
   */
  showSupervision?: boolean;
  /** Fourni ⇒ un bouton « copier le lien » apparaît dans l'en-tête, appelé avec le fichier courant. */
  onCopyLink?: (file: PreviewFile) => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<Element | null>(null);
  const overlayDismiss = useOverlayDismiss(onClose);

  /**
   * Position COURANTE **et** dernière direction de navigation, dans UN SEUL état.
   *
   * La direction vivait dans une `ref` LUE PENDANT LE RENDU (pour choisir le sens du
   * préchargement). C'est impur : une `ref` mutée hors cycle n'invalide pas le rendu, si bien
   * que React peut rendre avec un couple (index, direction) incohérent — et un rendu concurrent
   * ou rejoué (StrictMode, React Compiler) n'a aucune raison de les voir s'accorder. Les deux
   * valeurs changent TOUJOURS ensemble, à la même occasion : elles appartiennent au même état.
   */
  const clamp = (n: number): number => Math.min(Math.max(n, 0), files.length - 1);
  const [position, setPosition] = useState<{ index: number; direction: 'next' | 'prev' }>(() => ({
    index: clamp(startIndex),
    direction: 'next',
  }));
  const { index, direction } = position;

  const atFirst = index <= 0;
  const atLast = index >= files.length - 1;
  const goPrev = useCallback(() => {
    setPosition((p) => ({ index: Math.max(0, p.index - 1), direction: 'prev' }));
  }, []);
  const goNext = useCallback(() => {
    setPosition((p) => ({
      index: Math.min(files.length - 1, p.index + 1),
      direction: 'next',
    }));
  }, [files.length]);

  const file = files[clamp(index)]!;
  const category = fileCategory(file.mimeType, file.name);
  const previewable = file.hasContent && isPreviewable(file.mimeType);
  const contentUrl = urls.content(file);

  // Grandes vignettes `lg` à précharger : les quelques images situées dans la direction courante.
  const preloadTargets = preloadWindow(files, index, direction, PRELOAD_COUNT);

  // Verrou de défilement + focus initial (« Fermer ») + restauration à la fermeture.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (restoreFocusRef.current instanceof HTMLElement) restoreFocusRef.current.focus();
    };
  }, []);

  // Échap ferme ; ←/→ naviguent (sauf quand une vidéo a le focus, pour lui laisser le seek) ;
  // Tab boucle dans la modale (piège de focus).
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const active = document.activeElement;
        if (active instanceof HTMLVideoElement) return; // laisser la vidéo gérer le seek
        e.preventDefault();
        if (e.key === 'ArrowLeft') goPrev();
        else goNext();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), video, iframe, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose, goPrev, goNext],
  );

  // Indicateur de position vocalisé (« Fichier 3 sur 12 ») — t() n'interpole pas, on
  // substitue les repères ; l'affichage visuel reste le « 3 / 12 » compact et neutre.
  const positionLabel = fill(t('preview.position'), {
    current: formatCount(index + 1, locale),
    total: formatCount(files.length, locale),
  });

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        {...overlayDismiss}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={labelFor(t, t('preview.title'), file.name)}
          onKeyDown={onKeyDown}
          className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border p-3">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{file.name}</span>

            {/* Position dans le dossier : texte compact « 3 / 12 » à l'écran, libellé COMPLET
                pour les lecteurs d'écran.

                ⚠ CORRECTION (XFILE-50) : `aria-label` était posé sur un `<span>` NU. Un
                `aria-label` n'est honoré que sur un élément doté d'un rôle ; un `span` sans rôle
                est `generic`, et la spécification ARIA y interdit les noms accessibles — les
                lecteurs d'écran l'ignoraient donc, pendant que le contenu VISIBLE était
                `aria-hidden`. Résultat : « Fichier 3 sur 12 » n'était annoncé par AUCUN lecteur,
                contrairement à ce qu'affirmait le JSDoc de ce composant. Un texte `sr-only`
                réel, lui, est toujours lu. */}
            {files.length > 1 && (
              <span className="shrink-0 text-xs tabular-nums text-muted">
                <span className="sr-only">{positionLabel}</span>
                <span aria-hidden="true">
                  {index + 1} / {files.length}
                </span>
              </span>
            )}

            {onCopyLink && (
              <button
                type="button"
                onClick={() => onCopyLink(file)}
                aria-label={labelFor(t, t('preview.copyLink'), file.name)}
                title={t('preview.copyLink')}
                className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                <LinkIcon className="size-5" />
              </button>
            )}
            {file.hasContent && (
              <a
                href={contentUrl}
                download={file.name}
                aria-label={labelFor(t, t('preview.download'), file.name)}
                className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                <ArrowDownTrayIcon className="size-5" />
              </a>
            )}
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label={t('preview.close')}
              title={t('preview.close')}
              className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-stretch gap-2 bg-surface-2 p-3">
            <NavButton
              direction="prev"
              disabled={atFirst}
              onClick={goPrev}
              label={t('preview.previous')}
            />

            <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-auto">
              {!file.hasContent ? (
                <UnavailablePanel file={file} />
              ) : previewable ? (
                // Média REMONTÉ à chaque fichier (`key`) : le précédent disparaît AUSSITÔT et le
                // nouveau s'affiche en fondu au chargement (plus de « flash » du média précédent).
                // IMAGE (XFILE-26) : servie EXCLUSIVEMENT via la GRANDE vignette `lg` (WebP
                // web-optimisée 1600 px — rapide, et SEULE à rendre un HEIC dans un navigateur).
                // VIDÉO : contenu original (aucune vignette de vidéo n'existe).
                <MediaPanel
                  key={file.id}
                  file={file}
                  kind={category === 'video' ? 'video' : 'image'}
                  primarySrc={category === 'video' ? contentUrl : urls.thumbnail(file, 'lg')}
                />
              ) : (
                <UnsupportedPanel file={file} downloadUrl={contentUrl} openUrl={urls.open(file)} />
              )}
            </div>

            <NavButton
              direction="next"
              disabled={atLast}
              onClick={goNext}
              label={t('preview.next')}
            />
          </div>

          {/* PIED « propriétés » du fichier courant (jamais l'information par la couleur seule :
              chaque valeur porte son libellé + la provenance est un badge TEXTUEL). En usage
              PUBLIC (`showSupervision={false}`) provenance/consultations/dernière consultation
              sont masquées ; taille + type MIME restent TOUJOURS affichés. */}
          <FileProperties file={file} locale={locale} showSupervision={showSupervision} />

          {/* PRÉCHARGEMENT directionnel des grandes vignettes `lg` (fluidité Précédent/Suivant) :
              <img> DÉCORATIVES, hors flux visuel et hors ordre de tabulation. Le navigateur les
              télécharge et les met en cache → au changement de fichier `MediaPanel` réutilise
              l'entrée. Jamais annoncées (a11y), jamais `display:none` (sinon pas de fetch). */}
          <div
            data-testid="preview-preload"
            aria-hidden="true"
            className="pointer-events-none absolute size-px overflow-hidden opacity-0"
          >
            {preloadTargets.map((target) => (
              <img key={target.id} src={urls.thumbnail(target, 'lg')} alt="" decoding="async" />
            ))}
          </div>
        </div>
      </div>
    </Portal>
  );
}

/**
 * Média (image ou vidéo) du fichier COURANT avec état de CHARGEMENT : monté avec `key={file.id}`
 * par l'appelant, donc REMONTÉ à chaque changement de fichier — l'ancien média disparaît
 * immédiatement. Le nouveau reste en `opacity-0` jusqu'à `onLoad`/`onLoadedData`, où il apparaît
 * en fondu ; un indicateur de chargement (texte `sr-only`) couvre l'attente.
 *
 * IMAGE (XFILE-35) — `primarySrc` est la grande vignette `lg`, et RIEN d'autre : en échec, on
 * tombe DIRECTEMENT sur l'icône de type. Le repli vers l'ORIGINAL a été retiré — il faisait
 * télécharger une image de plusieurs mégapixels dès qu'une vignette manquait, alors que le serveur
 * la génère désormais à la demande et sert de toute façon une icône SVG en 200 plutôt qu'un 404.
 * (Le téléchargement explicite et « Ouvrir dans un nouvel onglet » servent toujours l'original.)
 * VIDÉO : `primarySrc` (l'original), aucune vignette de vidéo n'existant.
 */
function MediaPanel({
  file,
  kind,
  primarySrc,
}: {
  file: PreviewFile;
  kind: 'image' | 'video';
  primarySrc: string;
}): React.JSX.Element {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState(false);
  // Repli ultime en icône, réinitialisé par fichier (l'appelant remonte via `key={file.id}`).
  const [brokenImage, setBrokenImage] = useState(false);

  // Repli gracieux ULTIME : la vignette ne se rend pas → icône de type (le nom reste porté par
  // l'`aria-label` de la modale ; l'icône est décorative).
  if (kind === 'image' && brokenImage) {
    return (
      <div className="flex flex-col items-center justify-center p-6">
        <FileTypeIcon mimeType={file.mimeType} name={file.name} className="size-16" />
      </div>
    );
  }

  const fade = `max-h-[70vh] max-w-full rounded transition-opacity duration-150 ${
    loaded ? 'opacity-100' : 'opacity-0'
  }`;
  return (
    <div className="relative flex items-center justify-center">
      {!loaded && (
        <span role="status" className="absolute inset-0 flex items-center justify-center">
          <span className="sr-only">{t('storage.loading')}</span>
          <span
            aria-hidden="true"
            className="size-8 animate-spin rounded-full border-2 border-border border-t-brand"
          />
        </span>
      )}
      {kind === 'video' ? (
        <video
          src={primarySrc}
          controls
          aria-label={file.name}
          onLoadedData={() => setLoaded(true)}
          className={`${fade} object-contain`}
        />
      ) : (
        <img
          src={primarySrc}
          alt={file.name}
          onLoad={() => setLoaded(true)}
          onError={() => setBrokenImage(true)}
          className={`${fade} object-contain`}
        />
      )}
    </div>
  );
}

/**
 * Pied « propriétés » du fichier courant. Taille et type MIME sont TOUJOURS affichés (utiles au
 * destinataire public) ; provenance, consultations et dernière consultation sont des infos de
 * SUPERVISION masquées quand `showSupervision` est faux (usage public anonyme). Jamais
 * l'information par la couleur seule : chaque valeur porte son libellé, la provenance est un
 * badge TEXTUEL.
 */
function FileProperties({
  file,
  locale,
  showSupervision,
}: {
  file: PreviewFile;
  locale: string;
  showSupervision: boolean;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <dl
      aria-label={t('preview.properties')}
      className="flex flex-wrap gap-x-6 gap-y-1 border-t border-border p-3 text-xs text-muted"
    >
      {/* SUPERVISION : provenance (badge texte). */}
      {showSupervision && file.provenance !== undefined && (
        <div className="flex items-center gap-1.5">
          <dt className="font-medium text-fg">{t('preview.provenance')}</dt>
          <dd>
            <span className="rounded bg-surface-2 px-1.5 py-0.5 uppercase tracking-wide">
              {t(provenanceLabelKey[file.provenance])}
            </span>
          </dd>
        </div>
      )}
      {/* TAILLE : toujours affichée. */}
      <div className="flex items-center gap-1.5">
        <dt className="font-medium text-fg">{t('preview.size')}</dt>
        <dd>{formatBytes(file.sizeBytes)}</dd>
      </div>
      {/* SUPERVISION : nombre de consultations. */}
      {showSupervision && file.consultationCount !== undefined && (
        <div className="flex items-center gap-1.5">
          <dt className="font-medium text-fg">{t('preview.consultations')}</dt>
          <dd className="tabular-nums">{file.consultationCount}</dd>
        </div>
      )}
      {/* SUPERVISION : dernière consultation. */}
      {showSupervision && (
        <div className="flex items-center gap-1.5">
          <dt className="font-medium text-fg">{t('preview.lastConsulted')}</dt>
          <dd>
            {typeof file.lastConsultedAt === 'string'
              ? formatDateTime(file.lastConsultedAt, locale)
              : '—'}
          </dd>
        </div>
      )}
      {/* TYPE MIME : toujours affiché. */}
      <div className="flex items-center gap-1.5">
        <dt className="font-medium text-fg">{t('preview.mimeType')}</dt>
        <dd className="break-all">{file.mimeType}</dd>
      </div>
    </dl>
  );
}

/** Bouton de navigation latéral (précédent/suivant), inactif (disabled + aria) aux extrémités. */
function NavButton({
  direction,
  disabled,
  onClick,
  label,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
  label: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={label}
      title={label}
      className="flex shrink-0 items-center self-center rounded-md p-2 text-muted hover:bg-surface hover:text-fg disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
    >
      {direction === 'prev' ? (
        <ChevronLeftIcon className="size-6" />
      ) : (
        <ChevronRightIcon className="size-6" />
      )}
    </button>
  );
}

/**
 * Panneau « type non prévisualisable » (PDF inclus depuis T13) : icône de type + message + deux
 * actions — « Ouvrir dans un nouvel onglet » (URL inline durcie ; Chrome AFFICHE le PDF, les
 * autres types se téléchargent) ET « Télécharger ».
 */
function UnsupportedPanel({
  file,
  downloadUrl,
  openUrl,
}: {
  file: PreviewFile;
  downloadUrl: string;
  openUrl: string;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-4 p-6 text-center">
      <FileTypeIcon mimeType={file.mimeType} name={file.name} className="size-16" />
      <p className="text-sm text-muted">{t('preview.unsupported')}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <a
          href={openUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowTopRightOnSquareIcon className="size-5" />
          {t('preview.openInNewTab')}
        </a>
        <a
          href={downloadUrl}
          download={file.name}
          className="inline-flex items-center gap-2 rounded-md bg-brand-strong px-3 py-1.5 text-sm font-medium text-brand-fg hover:bg-brand-strong-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowDownTrayIcon className="size-5" />
          {t('preview.download')}
        </a>
      </div>
    </div>
  );
}

/** Panneau « contenu indisponible » : upload non écrit ou purgé — ni aperçu ni téléchargement. */
function UnavailablePanel({ file }: { file: PreviewFile }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-4 p-6 text-center">
      <FileTypeIcon mimeType={file.mimeType} name={file.name} className="size-16" />
      <p className="text-sm text-muted">{t('preview.unavailable')}</p>
    </div>
  );
}
